/**
 * 「招待コードを入れる」だけの画面（/start-creating・2026-10-07 console/one-account・設計 §1）。旗 oneAccount が on で、
 * 作り手の中身が無い人の Creator はこれだけ（App.tsx）。サーバーの invite_code_required の details.signupUrl（/signup）もここへ寄せる。
 *
 *   POST /v1/account/creator { inviteCode? }（kas_）→ 201 { developerToken, developer } → 作り手として入り、元の画面（state.from）か / へ。
 *   断られたときの案内（oneAccount.ts の creatorStartIssue）:
 *     email_required       メールの無いアカウント → アカウントのページでメールを足してから、もう一度押す
 *     creator_email_in_use 同じメールの作り手がもういて、自動でつなげなかった（片方の確認がサービス任せ・ぶつかる物がある）→ ログアウトして前の方法で入り直し「つなぐ」
 *   同じメールの作り手がいて自動でつなげたとき（2026-10-08 founder「自動でつなぐ形にしよう」）は 200 { …, connected: true } = 作り手を始めたときと同じく Creator を開く
 *     already_creator      もう作り手（別のタブ等）→ 入り直せば Creator が開く
 *     invite               コードの誤り・使用済み等 → 既存の文言（error.<code>・en.invite.ts）。招待制の本体だけ（2026-10-29 から下の notApplied）
 *   招待なしの本体（KUMO_SIGNUP_REQUIRES_INVITE=false・2026-10-29 core/open-signup-build）は使えないコードでも 201 で作り inviteNotApplied を返す →
 *     「使えませんでしたが、できました」の 1 枚（components/InviteNotAppliedNotice.tsx）を出してから進む
 */
import { startTransition, useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, type Developer } from '../api';
import { startAccountSignIn, startCreating } from '../accountApi';
import { afterLoginPath } from '../authForms';
import { loadSignupInviteRequirement } from '../devOAuthClient';
import { errorText, useT } from '../i18n';
import { inviteNotAppliedReason, readInviteInput, type InviteError, type SignupInviteRequirement } from '../inviteCode';
import { INVITE_CODE_PATH, InviteNotAppliedNotice } from '../components/InviteNotAppliedNotice';
import { accountPageUrl, creatorStartIssue, saveMode, type CreatorStartIssue } from '../oneAccount';
import { useSession } from '../session';
import { AccountTopBar } from './PlayerPage';

export function CreatorStartPage() {
  const { t } = useT();
  const { account, oneAccount, signIn, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  const [requirement, setRequirement] = useState<SignupInviteRequirement>('unknown');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState<{ kind: CreatorStartIssue; text?: string } | null>(null);
  // 使えないコードだったが作り手はできた（招待なしの本体・2026-10-29）。まだ signIn しない（AuthPage の notApplied と同じ理由 = この画面が消える）
  const [notApplied, setNotApplied] = useState<{ reason: InviteError | 'unknown'; developerToken: string; developer: Developer } | null>(null);
  const finish = (developerToken: string, developer: Developer, to: string) => {
    startTransition(() => {
      navigate(to, { replace: true });
      signIn(developerToken, developer);
    });
  };

  useEffect(() => {
    void loadSignupInviteRequirement().then(setRequirement);
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!account) return;
    setIssue(null);
    const input = readInviteInput(code);
    // 形の違うコードはその場で止める（送っても断られるだけ）。招待制で空でも送る（2026-10-08 core/one-account-3）: 同じメールの今の作り手がいれば
    // サーバーが招待コード無しで自動でつなぐ。つなげない人には invite_code_required が返り、下の catch で同じ文言を出す
    if (input.kind === 'invalid') return setIssue({ kind: 'invite', text: t('error.invalid_invite_code') });
    setBusy(true);
    try {
      const r = await startCreating(account.token, input.kind === 'ok' ? input.code : null);
      // 作り手を始めた人は Creator を開く（覚える方も Creator に）
      saveMode(safeLocal(), 'creator');
      const reason = inviteNotAppliedReason(r);
      if (reason) {
        setNotApplied({ reason, developerToken: r.developerToken, developer: r.developer as Developer });
        setBusy(false);
        return;
      }
      finish(r.developerToken, r.developer as Developer, afterLoginPath(from));
    } catch (err) {
      const kind = creatorStartIssue(err instanceof ApiError ? err.code : undefined);
      setIssue({ kind, text: kind === 'invite' || kind === 'other' ? (err instanceof ApiError ? errorText(err) : String(err)) : undefined });
      setBusy(false);
    }
  };

  const accountPage = oneAccount.accountUrl ? accountPageUrl(oneAccount.accountUrl) : null;

  if (notApplied) {
    return (
      <div className="page-wrap">
        <AccountTopBar />
        <main className="container narrow" id="main">
          <InviteNotAppliedNotice
            reason={notApplied.reason}
            onContinue={() => finish(notApplied.developerToken, notApplied.developer, afterLoginPath(from))}
            onBilling={() => finish(notApplied.developerToken, notApplied.developer, INVITE_CODE_PATH)}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="page-wrap">
      <AccountTopBar />
      <main className="container narrow" id="main">
        <h1>{t('creatorStart.title')}</h1>
        <p className="muted">{t(requirement === 'optional' ? 'creatorStart.leadOptional' : 'creatorStart.leadRequired')}</p>
        {issue?.kind === 'email_required' && (
          <div className="alert alert-warn" role="alert">
            <p>{t('creatorStart.emailRequired')}</p>
            {accountPage && (
              <a className="btn btn-sm" href={accountPage} target="_blank" rel="noopener noreferrer">
                {t('oneAccount.openAccountPage')}
              </a>
            )}
          </div>
        )}
        {issue?.kind === 'creator_email_in_use' && (
          <div className="alert alert-warn" role="alert">
            <p>{t('creatorStart.emailInUse', { email: account?.email ?? '' })}</p>
            <button type="button" className="btn btn-sm" onClick={() => void signOut()}>
              {t('creatorStart.signOut')}
            </button>
          </div>
        )}
        {issue?.kind === 'already_creator' && (
          <div className="alert alert-warn" role="alert">
            <p>{t('creatorStart.already')}</p>
            {oneAccount.accountUrl && (
              <button type="button" className="btn btn-sm" onClick={() => void startAccountSignIn(oneAccount.accountUrl!, '/')}>
                {t('oneAccount.callback.again')}
              </button>
            )}
          </div>
        )}
        {issue?.text && (
          <p className="alert alert-error" role="alert">
            {issue.text}
          </p>
        )}
        <form className="card" onSubmit={(e) => void submit(e)}>
          <label className="field" htmlFor="creator-invite">
            <span>{t('invite.field')}</span>
            <input
              id="creator-invite"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              // required は付けない（2026-10-08 core/one-account-3）: 同じメールの今の作り手がいる人は空のまま押せば自動でつながる。
              // 空で押した招待制の新しい人には、サーバーの invite_code_required を submit の catch が出す
            />
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy || !account}>
            {busy ? t('creatorStart.busy') : t('creatorStart.submit')}
          </button>
        </form>
      </main>
    </div>
  );
}

function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
